<template>
  <ContactReviewDisplayComponent
    :accountData="accountData"
    :accountRules="accountRules"
    :companyRules="companyRules"
    :companyIndex="companyIndex"
    :countryCode="'EE'"
    @updateAccountData="$emit('updateAccountData', $event)"
    @handleUpdateCompany="$emit('handleUpdateCompany', $event)"
  >
    <template #account="{ phoneCountryCode, accountField }">
      <TextRowComponent v-bind="accountField('name')" />
      <TextRowComponent v-bind="accountField('registry_code')" />
      <TextRowComponent
        v-bind="accountField('phone_number')"
        type="tel"
        :countryCode="phoneCountryCode"
      />
    </template>
    <template #company="{ locked, companyField }">
      <TextRowComponent v-bind="companyField('name')" :disabled="locked" />
      <TextRowComponent
        v-bind="companyField('registry_code')"
        :disabled="locked"
      />
      <TextRowComponent v-bind="companyField('address')" :disabled="locked" />
      <TextRowComponent v-bind="companyField('vat')" />
      <TextRowComponent
        v-bind="companyField('contact_email', accountData.email)"
        type="email"
      />
      <TextRowComponent
        v-bind="companyField('invoice_email', accountData.email)"
        type="email"
      />
    </template>
  </ContactReviewDisplayComponent>
</template>

<script setup lang="ts">
import { useValidators } from "@/composables/useValidators";
import type { AccountRules, CompanyRules } from "@/signup/common/contactFields";
import TextRowComponent from "@/signup/common/TextRowComponent.vue";
import ContactReviewDisplayComponent from "@/signup/ContactReviewDisplayComponent.vue";
import type { Account, Company } from "@/types";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
  accountData: Account;
  companyIndex?: number;
}>();

defineEmits<{
  updateAccountData: [accountData: Account];
  handleUpdateCompany: [company: Company];
}>();

const { t } = useI18n();
const {
  required,
  email,
  estonianPersonalID,
  estonianIDorCompanyCode,
  estonianYourPersonalIDifID,
  estonianVATNumber,
  personalName,
  phoneNumber,
} = useValidators(t);

const accountRules: AccountRules = {
  name: { required, personalName },
  registry_code: { required, estonianPersonalID },
  phone_number: { required, phoneNumber },
};

const companyRules: CompanyRules = {
  name: { required },
  registry_code: {
    required,
    estonianIDorCompanyCode,
    estonianYourPersonalIDifID: estonianYourPersonalIDifID(
      computed(() => props.accountData.registry_code)
    ),
  },
  address: { required },
  vat: { estonianVATNumber },
  contact_email: { email },
  invoice_email: { email },
};
</script>

<style scoped lang="scss"></style>
